/**
 * Shared helper functions for creating tool capability infrastructure records.
 * These records are common across Script, Subflow, and FlowAction tools:
 *   - sys_one_extend_capability
 *   - sys_one_extend_capability_definition
 *   - sys_one_extend_definition_config
 *   - sys_gen_ai_feature_mapping
 *   - sys_gen_ai_strategy_mapping
 */

import type { Context, ObjectShape, Record as BuildRecord } from '@servicenow/sdk-build-core'
import { applyDefaults } from '../skill-plugin-helpers'
import type { ToolDefinition } from '../tool-types'
import {
    SYS_ONE_EXTEND_CAPABILITY,
    SYS_ONE_EXTEND_CAPABILITY_DEFINITION,
    SYS_ONE_EXTEND_DEFINITION_CONFIG,
    SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
    SYS_GEN_AI_FEATURE_MAPPING,
    SYS_GEN_AI_STRATEGY_MAPPING,
    SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
    generateInputAttributeValueScript,
    normalizeInputFieldName,
} from '../tool-utils'

/**
 * Configuration for creating a tool capability definition record
 */
export interface ToolCapabilityDefinitionConfig {
    /** Display name for the capability definition */
    name: string
    /** API identifier (e.g., actionId, subflowId, SCRIPT_TOOL_API_ID) */
    api: string
    /** API type (e.g., sys_hub_action_type_definition, sys_hub_flow) */
    apiType: string
    /** JSON string for metadata field */
    metadata?: string
}

/**
 * Result of creating tool capability infrastructure records
 */
export interface ToolCapabilityInfraResult {
    /** All created records in order */
    records: BuildRecord[]
    /** The tool's capability record */
    toolCapabilityRecord: BuildRecord
    /** The explicitId used for the tool's capability record */
    toolCapabilityExplicitId: string
    /** The tool's capability definition record */
    toolCapDefRecord: BuildRecord
    /** The feature mapping record */
    featureMappingRecord: BuildRecord
}

/**
 * Create the common capability infrastructure records for a tool.
 *
 * Creates the following records:
 * 1. sys_one_extend_capability - Tool capability
 * 2. sys_one_extend_capability_definition - Capability definition
 * 3. sys_one_extend_definition_config - Links capability and definition
 * 4. sys_gen_ai_feature_mapping - Feature mapping
 * 5. sys_gen_ai_strategy_mapping - Strategy mapping
 *
 * @param tool - The tool definition
 * @param capabilityRecord - Parent skill's capability record (used for explicitId derivation)
 * @param capabilityName - Name for the tool's capability
 * @param capDefConfig - Configuration for the capability definition record
 * @param arg - ObjectShape for transform context
 * @param factory - Record factory
 * @returns Created records and key record references
 */
export async function createToolCapabilityInfra(
    tool: ToolDefinition,
    capabilityRecord: BuildRecord,
    capabilityName: string,
    capDefConfig: ToolCapabilityDefinitionConfig,
    arg: ObjectShape,
    factory: Context['factory']
): Promise<ToolCapabilityInfraResult> {
    const records: BuildRecord[] = []

    // 1. Create sys_one_extend_capability record
    // When tool.$capabilityId is present, use it directly as the capability explicitId.
    // Otherwise fall back to skill capability + tool name derivation.
    const toolCapabilityExplicitId = tool.$capabilityId
        ? tool.$capabilityId
        : `${capabilityRecord.getId().toString().getValue()}__tool_${tool.name}`
    const toolCapabilityRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_ONE_EXTEND_CAPABILITY,
        explicitId: toolCapabilityExplicitId,
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_ONE_EXTEND_CAPABILITY, $),
            name: $.val(capabilityName),
            type: $.val('datasource'),
        })),
    })
    records.push(toolCapabilityRecord)

    // 2. Create sys_one_extend_capability_definition record
    const toolCapDefRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_ONE_EXTEND_CAPABILITY_DEFINITION,
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_ONE_EXTEND_CAPABILITY_DEFINITION, $),
            name: $.val(capDefConfig.name),
            capability: $.val(toolCapabilityRecord.getId()),
            api: $.val(capDefConfig.api),
            api_type: $.val(capDefConfig.apiType),
            ...(capDefConfig.metadata ? { metadata: $.val(capDefConfig.metadata) } : {}),
        })),
    })
    records.push(toolCapDefRecord)

    // 3. Create sys_one_extend_definition_config record
    const toolDefConfigRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_ONE_EXTEND_DEFINITION_CONFIG,
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_ONE_EXTEND_DEFINITION_CONFIG, $),
            capability: $.val(toolCapabilityRecord.getId()),
            definition: $.val(toolCapDefRecord.getId()),
            default: $.val('1'),
        })),
    })
    records.push(toolDefConfigRecord)

    // 4. Create sys_gen_ai_feature_mapping record
    const featureMappingRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_GEN_AI_FEATURE_MAPPING,
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_GEN_AI_FEATURE_MAPPING, $),
            document: $.val(toolCapabilityRecord.getId()),
            document_table: $.val('sys_one_extend_capability'),
            feature_name: $.val(capabilityName),
        })),
    })
    records.push(featureMappingRecord)

    // 5. Create sys_gen_ai_strategy_mapping record
    const strategyMappingRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_GEN_AI_STRATEGY_MAPPING,
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_GEN_AI_STRATEGY_MAPPING, $),
            feature: $.val(featureMappingRecord.getId()),
            strategy: $.val('CAPABILITY_EXECUTION'),
        })),
    })
    records.push(strategyMappingRecord)

    return {
        records,
        toolCapabilityRecord,
        toolCapabilityExplicitId,
        toolCapDefRecord,
        featureMappingRecord,
    }
}

/**
 * Create sys_one_extend_definition_attribute records for tool inputs.
 * Common pattern shared by Script and Subflow tools.
 *
 * @param tool - The tool definition
 * @param toolCapabilityRecord - The tool's capability record
 * @param arg - ObjectShape for transform context
 * @param factory - Record factory
 * @returns Map of input name -> definition attribute record, and created records array
 */
export async function createInputDefinitionAttributes(
    tool: ToolDefinition,
    toolCapabilityRecord: BuildRecord,
    arg: ObjectShape,
    factory: Context['factory']
): Promise<{ inputDefAttrRecordsMap: Map<string, BuildRecord>; records: BuildRecord[] }> {
    const records: BuildRecord[] = []
    const inputDefAttrRecordsMap = new Map<string, BuildRecord>()

    if (tool.inputs) {
        for (const input of tool.inputs) {
            const internalName = normalizeInputFieldName(input.name)
            const inputDefAttrRecord = await factory.createRecord({
                source: tool._source!,
                table: SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
                explicitId: input.$id,
                properties: arg.transform(({ $ }) => ({
                    ...applyDefaults(SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE, $),
                    capability: $.val(toolCapabilityRecord.getId()),
                    name: $.val(internalName),
                    label: $.val(input.name),
                    type: $.val('input'),
                    ...(input.type ? { data_type: $.val(input.type) } : {}),
                })),
            })
            records.push(inputDefAttrRecord)
            inputDefAttrRecordsMap.set(input.name, inputDefAttrRecord)
        }
    }

    return { inputDefAttrRecordsMap, records }
}

/**
 * Create sys_one_extend_resource_attribute_mapping records for tool inputs.
 * Common pattern shared by Script, Subflow, and FlowAction tools.
 *
 * @param tool - The tool definition
 * @param resourceMappingRecord - The tool's resource mapping record
 * @param arg - ObjectShape for transform context
 * @param factory - Record factory
 * @param inputDefAttrRecordsMap - Optional map of input name -> definition attribute record (for Script/Subflow)
 * @returns Created input attribute mapping records
 */
export async function createInputAttributeMappings(
    tool: ToolDefinition,
    resourceMappingRecord: BuildRecord,
    arg: ObjectShape,
    factory: Context['factory'],
    inputDefAttrRecordsMap?: Map<string, BuildRecord>
): Promise<BuildRecord[]> {
    const records: BuildRecord[] = []

    if (tool.inputs) {
        for (const input of tool.inputs) {
            const isScripted = input.valueType === 'script'
            const inputScript = isScripted
                ? input.scriptValue!
                : generateInputAttributeValueScript(input.name, input.value)
            const inputDefAttrRecord = inputDefAttrRecordsMap?.get(input.name)
            const inputAttributeRecord = await factory.createRecord({
                source: tool._source!,
                table: SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
                properties: arg.transform(({ $ }) => ({
                    ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING, $),
                    attribute_value_script: $.val(inputScript).toCdata(),
                    attribute_value_template: $.val(input.value),
                    attribute_value_type: $.val(isScripted ? 'script' : 'template'),
                    resource_attribute_name: inputDefAttrRecord ? $.val(inputDefAttrRecord.getId()) : $.val(input.name),
                    resource_mapping: $.val(resourceMappingRecord.getId()),
                    type: $.val('input'),
                })),
            })
            records.push(inputAttributeRecord)
        }
    }

    return records
}
